import * as THREE from 'three';
import type { IslandCell, PropPlacement } from '../../shared/islandField';
import type { InstanceTransform, ModelLibrary } from './ModelLibrary';

/**
 * Deterministic natural-prop rendering for one island.
 *
 * `shared/islandField.ts` derives the placement list (rocks, palms, and — on
 * habitable islands — a small settlement) from the island seed. This module
 * groups those placements by prop type and hands each group to
 * `ModelLibrary.instance`, so every prop type renders as a single
 * `InstancedMesh` (one draw call per type per island).
 *
 * The builder is idempotent per type: it tags each instanced mesh with
 * `island-prop-<type>` and skips a type that is already built, so the streamer
 * can retry safely while the GLB library is still loading without duplicating
 * meshes.
 */

export interface IslandPropsOptions {
  castShadow?: boolean;
}

export interface IslandPropsBuildResult {
  /** Number of distinct prop types on this island (0 when the island is bare). */
  totalTypes: number;
  /** Number of types whose `InstancedMesh` was built in this pass. */
  builtTypes: number;
}

/**
 * Build (or top up) instanced props for one island in island-local space.
 * `placements` is the deterministic list from `islandProps(cell)`; the caller
 * derives it once so retries while the GLB library loads stay cheap.
 */
export function buildIslandProps(
  cell: IslandCell,
  placements: PropPlacement[],
  modelLibrary: ModelLibrary,
  group: THREE.Group,
  options: IslandPropsOptions = {},
): IslandPropsBuildResult {
  const byType = new Map<string, InstanceTransform[]>();

  for (const placement of placements) {
    let list = byType.get(placement.type);
    if (!list) {
      list = [];
      byType.set(placement.type, list);
    }
    list.push({
      position: new THREE.Vector3(placement.x - cell.x, placement.y, placement.z - cell.z),
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, placement.yaw, 0)),
      scale: placement.scale,
    });
  }

  let builtTypes = 0;
  for (const [type, transforms] of byType) {
    const name = `island-prop-${type}`;
    if (group.getObjectByName(name)) {
      builtTypes += 1;
      continue;
    }
    const mesh = modelLibrary.instance(type, transforms, group, {
      castShadow: options.castShadow ?? true,
      receiveShadow: false,
    });
    if (mesh) {
      mesh.name = name;
      builtTypes += 1;
    }
  }

  return { totalTypes: byType.size, builtTypes };
}
